import { usDateFormatter } from 'admin/lib/Functions/timeZoneHelper'
import {
  IMPERIAL_VOLUME_UNITS,
  IMPERIAL_WEIGHT_UNITS,
  METRIC_VOLUME_UNITS,
  METRIC_WEIGHT_UNITS,
  MeasurementUoM
} from 'admin/lib/Records/constants'

type QcrMessage = {
  level: string
  message: string
}

export const recordTransformer = (
  record: { data: any; raw: any; enriched: any; messages?: QcrMessage[] },
  moduleName: string,
  isImperial: boolean
) => {
  if (record && record.messages) {
    record.messages.forEach(message => {
      console.debug('QCR', message)
    })
  }

  // remap properties to fields for each line item
  const localRecord =
    record && record.data && record.data.products
      ? {
          ...record,
          data: { ...record.data, products: [...record.data.products] },
          raw: {},
          enriched: {}
        }
      : record && record.data && record.data.items
        ? {
            ...record,
            data: { ...record.data, items: [...record.data.items] },
            raw: {},
            enriched: {}
          }
        : record

  // Add RAW values if present.
  if (localRecord && localRecord.raw) {
    localRecord.raw =
      record && record.raw && record.raw.products
        ? { ...record.raw, products: [...record.raw.products] }
        : record && record.raw && record.raw.items
          ? { ...record.raw, items: [...record.raw.items] }
          : {}
  }

  // Add ENRICHED values if present.
  if (localRecord && localRecord.enriched) {
    localRecord.enriched =
      record && record.enriched && record.enriched.products
        ? { ...record.enriched, products: [...record.enriched.products] }
        : record && record.enriched && record.enriched.items
          ? { ...record.enriched, items: [...record.enriched.items] }
          : {}
  }

  let recordItems: any
  let recordProducts: any

  // Keeping the following for backwards support (i.e pre-Raw & Enriched)
  if (
    record &&
    record.data &&
    record.data.products &&
    moduleName === 'Delivery'
  ) {
    recordProducts = localRecord.data.products.map((product: any) => {
      const remappedProduct = { ...product }

      if (!remappedProduct.quantity) {
        remappedProduct.quantity = ''
      }

      // Handle Imperial/US records
      if (isImperial) {
        // Height
        if (product.height) {
          remappedProduct.heightYards = ''
          remappedProduct.heightFeet = ''
          remappedProduct.heightInches = ''

          product.height.forEach(item => {
            switch (item.unit) {
              case 'Yard':
                remappedProduct.heightYards = item.value
                break
              case 'Foot':
                remappedProduct.heightFeet = item.value
                break
              case 'Inch':
                remappedProduct.heightInches = item.value
                break
            }
          })
          delete remappedProduct.height
        }
        // Width
        if (product.width) {
          remappedProduct.widthYards = ''
          remappedProduct.widthFeet = ''
          remappedProduct.widthInches = ''
          product.width.forEach(item => {
            switch (item.unit) {
              case 'Yard':
                remappedProduct.widthYards = item.value
                break
              case 'Foot':
                remappedProduct.widthFeet = item.value
                break
              case 'Inch':
                remappedProduct.widthInches = item.value
                break
            }
          })
          delete remappedProduct.width
        }
        // Length
        if (product.length) {
          remappedProduct.lengthYards = ''
          remappedProduct.lengthFeet = ''
          remappedProduct.lengthInches = ''
          product.length.forEach(item => {
            switch (item.unit) {
              case 'Yard':
                remappedProduct.lengthYards = item.value
                break
              case 'Foot':
                remappedProduct.lengthFeet = item.value
                break
              case 'Inch':
                remappedProduct.lengthInches = item.value
                break
            }
          })
          delete remappedProduct.length
        }
        // ProductionTimeUS
        if (product.productionTime) {
          remappedProduct.productionTimeUS = usDateFormatter(
            product.productionTime
          )
        }
        // UsedAtTimeUS
        if (product.usedAtTime) {
          remappedProduct.usedAtTimeUS = usDateFormatter(product.productionTime)
        }
      } else {
        // Height
        if (Array.isArray(product.height) && product.height[0]) {
          remappedProduct.height = product.height[0].value
          remappedProduct.heightUoM = product.height[0].unit
        } else {
          remappedProduct.heightUoM = MeasurementUoM[0].name
          remappedProduct.height = ''
        }
        // Width
        if (Array.isArray(product.width) && product.width[0]) {
          remappedProduct.width = product.width[0].value
          remappedProduct.widthUoM = product.width[0].unit
        } else {
          remappedProduct.widthUoM = MeasurementUoM[0].name
          remappedProduct.width = ''
        }
        // Length
        if (Array.isArray(product.length) && product.length[0]) {
          remappedProduct.length = product.length[0].value
          remappedProduct.lengthUoM = product.length[0].unit
        } else {
          remappedProduct.lengthUoM = MeasurementUoM[0].name
          remappedProduct.length = ''
        }
      }

      // Calculated Volume
      if (
        Array.isArray(product.calculatedVolume) &&
        product.calculatedVolume[0]
      ) {
        remappedProduct.calculatedVolume = product.calculatedVolume[0].value
        if (isImperial && product.calculatedVolume[0].unit === 'CubicMeter') {
          // Convert value to Cubic Yards
          remappedProduct.calculatedVolume =
            remappedProduct.calculatedVolume * 1.30795
        }
      } else {
        delete remappedProduct.calculatedVolume
      }
      // Given Volume
      if (Array.isArray(product.givenVolume) && product.givenVolume[0]) {
        remappedProduct.givenVolume = product.givenVolume[0].value
        remappedProduct.givenVolumeUoM = product.givenVolume[0].unit
      } else {
        remappedProduct.givenVolumeUoM = !isImperial
          ? METRIC_VOLUME_UNITS[0].name
          : IMPERIAL_VOLUME_UNITS[0].name
        remappedProduct.givenVolume = ''
      }
      // Estimated Weight
      if (
        Array.isArray(product.estimatedWeight) &&
        product.estimatedWeight[0]
      ) {
        remappedProduct.estimatedWeight = product.estimatedWeight[0].value
        if (isImperial && product.estimatedWeight[0].unit === 'Tonne') {
          // Convert value to Pounds (lbs)
          remappedProduct.estimatedWeight =
            remappedProduct.estimatedWeight * 2204.6226
        }
      } else {
        delete remappedProduct.estimatedWeight
      }
      // Given Weight
      if (Array.isArray(product.weight) && product.weight[0]) {
        remappedProduct.weight = product.weight[0].value
        remappedProduct.weightUoM = product.weight[0].unit
      } else {
        remappedProduct.weightUoM = !isImperial
          ? METRIC_WEIGHT_UNITS[0].name
          : IMPERIAL_WEIGHT_UNITS[0].name
        remappedProduct.weight = ''
      }

      // Product Group(s)
      if (Array.isArray(product.productGroups)) {
        remappedProduct.productGroups = product.productGroups[0]
          ? `${product.productGroups[0].productGroupId}:${product.productGroups[0].name}`
          : null
      }
      return remappedProduct
    })

    localRecord.data.products = recordProducts
  } else if (
    record &&
    record.data &&
    record.data.items &&
    moduleName === 'Waste'
  ) {
    // If Module is 'Waste' remap units for Volume and Weight
    recordItems = localRecord.data.items.map((item: any) => {
      const remappedItem = { ...item }

      if (!remappedItem.quantity) {
        remappedItem.quantity = ''
      }

      // Volume Estimated
      if (Array.isArray(item.volumeEstimated) && item.volumeEstimated[0]) {
        remappedItem.volumeEstimated = item.volumeEstimated[0].value
      } else {
        delete remappedItem.volumeEstimated
      }
      // (Given) Volume
      if (Array.isArray(item.volume) && item.volume[0]) {
        remappedItem.volume = item.volume[0].value
        remappedItem.volumeUoM = item.volume[0].unit
      } else {
        remappedItem.volumeUoM = !isImperial
          ? METRIC_VOLUME_UNITS[0].name
          : IMPERIAL_VOLUME_UNITS[0].name
        remappedItem.volume = ''
      }
      // Weight Estimated
      if (Array.isArray(item.weightEstimated) && item.weightEstimated[0]) {
        remappedItem.weightEstimated = item.weightEstimated[0].value
      } else {
        delete remappedItem.weightEstimated
      }
      // (Given) Weight
      if (Array.isArray(item.weight) && item.weight[0]) {
        remappedItem.weight = item.weight[0].value
        remappedItem.weightUoM = item.weight[0].unit
      } else {
        remappedItem.weightUoM = !isImperial
          ? METRIC_WEIGHT_UNITS[0].name
          : IMPERIAL_WEIGHT_UNITS[0].name
        remappedItem.weight = ''
      }
      return remappedItem
    })

    localRecord.data.items = recordItems
  }

  // Remapping Enriched Unit Based Values (e.g. Width, Length, Height, Volume, Weight)
  if (
    record &&
    record.enriched &&
    record.enriched.products &&
    moduleName === 'Delivery'
  ) {
    recordProducts = localRecord.enriched.products.map((product: any) => {
      const remappedProduct = { ...product }

      if (!remappedProduct.quantity) {
        remappedProduct.quantity = ''
      }

      // Handle Imperial/US records
      if (isImperial) {
        // Height
        if (product.height) {
          remappedProduct.heightYards = ''
          remappedProduct.heightFeet = ''
          remappedProduct.heightInches = ''

          product.height.forEach(item => {
            switch (item.unit) {
              case 'Yard':
                remappedProduct.heightYards = item.value
                break
              case 'Foot':
                remappedProduct.heightFeet = item.value
                break
              case 'Inch':
                remappedProduct.heightInches = item.value
                break
            }
          })
          delete remappedProduct.height
        }
        // Width
        if (product.width) {
          remappedProduct.widthYards = ''
          remappedProduct.widthFeet = ''
          remappedProduct.widthInches = ''
          product.width.forEach(item => {
            switch (item.unit) {
              case 'Yard':
                remappedProduct.widthYards = item.value
                break
              case 'Foot':
                remappedProduct.widthFeet = item.value
                break
              case 'Inch':
                remappedProduct.widthInches = item.value
                break
            }
          })
          delete remappedProduct.width
        }
        // Length
        if (product.length) {
          remappedProduct.lengthYards = ''
          remappedProduct.lengthFeet = ''
          remappedProduct.lengthInches = ''
          product.length.forEach(item => {
            switch (item.unit) {
              case 'Yard':
                remappedProduct.lengthYards = item.value
                break
              case 'Foot':
                remappedProduct.lengthFeet = item.value
                break
              case 'Inch':
                remappedProduct.lengthInches = item.value
                break
            }
          })
          delete remappedProduct.length
        }
        // ProductionTimeUS
        if (product.productionTime) {
          remappedProduct.productionTimeUS = usDateFormatter(
            product.productionTime
          )
        }
        // UsedAtTimeUS
        if (product.usedAtTime) {
          remappedProduct.usedAtTimeUS = usDateFormatter(product.productionTime)
        }
      } else {
        // Height
        if (Array.isArray(product.height) && product.height[0]) {
          remappedProduct.height = product.height[0].value
          remappedProduct.heightUoM = product.height[0].unit
        } else {
          remappedProduct.heightUoM = MeasurementUoM[0].name
          remappedProduct.height = ''
        }
        // Width
        if (Array.isArray(product.width) && product.width[0]) {
          remappedProduct.width = product.width[0].value
          remappedProduct.widthUoM = product.width[0].unit
        } else {
          remappedProduct.widthUoM = MeasurementUoM[0].name
          remappedProduct.width = ''
        }
        // Length
        if (Array.isArray(product.length) && product.length[0]) {
          remappedProduct.length = product.length[0].value
          remappedProduct.lengthUoM = product.length[0].unit
        } else {
          remappedProduct.lengthUoM = MeasurementUoM[0].name
          remappedProduct.length = ''
        }
      }

      // Calculated Volume
      if (
        Array.isArray(product.calculatedVolume) &&
        product.calculatedVolume[0]
      ) {
        remappedProduct.calculatedVolume = product.calculatedVolume[0].value
        if (isImperial && product.calculatedVolume[0].unit === 'CubicMeter') {
          // Convert value to Cubic Yards
          remappedProduct.calculatedVolume =
            remappedProduct.calculatedVolume * 1.30795
        }
      } else {
        delete remappedProduct.calculatedVolume
      }
      // Given Volume
      if (Array.isArray(product.givenVolume) && product.givenVolume[0]) {
        remappedProduct.givenVolume = product.givenVolume[0].value
        remappedProduct.givenVolumeUoM = product.givenVolume[0].unit
      } else {
        remappedProduct.givenVolumeUoM = !isImperial
          ? METRIC_VOLUME_UNITS[0].name
          : IMPERIAL_VOLUME_UNITS[0].name
        remappedProduct.givenVolume = ''
      }
      // Estimated Weight
      if (
        Array.isArray(product.estimatedWeight) &&
        product.estimatedWeight[0]
      ) {
        remappedProduct.estimatedWeight = product.estimatedWeight[0].value
        if (isImperial && product.estimatedWeight[0].unit === 'Tonne') {
          // Convert value to Pounds (lbs)
          remappedProduct.estimatedWeight =
            remappedProduct.estimatedWeight * 2204.6226
        }
      } else {
        delete remappedProduct.estimatedWeight
      }
      // Given Weight
      if (Array.isArray(product.weight) && product.weight[0]) {
        remappedProduct.weight = product.weight[0].value
        remappedProduct.weightUoM = product.weight[0].unit
      } else {
        remappedProduct.weightUoM = !isImperial
          ? METRIC_WEIGHT_UNITS[0].name
          : IMPERIAL_WEIGHT_UNITS[0].name
        remappedProduct.weight = ''
      }

      // Product Group(s)
      if (Array.isArray(product.productGroups)) {
        remappedProduct.productGroups = product.productGroups[0]
          ? `${product.productGroups[0].productGroupId}:${product.productGroups[0].name}`
          : null
      }
      return remappedProduct
    })

    localRecord.enriched.products = recordProducts
  } else if (
    record &&
    record.enriched &&
    record.enriched.items &&
    moduleName === 'Waste'
  ) {
    // If Module is 'Waste' remap units for Volume and Weight
    recordItems = localRecord.enriched.items.map((item: any) => {
      const remappedItem = { ...item }

      if (!remappedItem.quantity) {
        remappedItem.quantity = ''
      }

      // Volume Estimated
      if (Array.isArray(item.volumeEstimated) && item.volumeEstimated[0]) {
        remappedItem.volumeEstimated = item.volumeEstimated[0].value
      } else {
        delete remappedItem.volumeEstimated
      }
      // (Given) Volume
      if (Array.isArray(item.volume) && item.volume[0]) {
        remappedItem.volume = item.volume[0].value
        remappedItem.volumeUoM = item.volume[0].unit
      } else {
        remappedItem.volumeUoM = !isImperial
          ? METRIC_VOLUME_UNITS[0].name
          : IMPERIAL_VOLUME_UNITS[0].name
        remappedItem.volume = ''
      }
      // Weight Estimated
      if (Array.isArray(item.weightEstimated) && item.weightEstimated[0]) {
        remappedItem.weightEstimated = item.weightEstimated[0].value
      } else {
        delete remappedItem.weightEstimated
      }
      // (Given) Weight
      if (Array.isArray(item.weight) && item.weight[0]) {
        remappedItem.weight = item.weight[0].value
        remappedItem.weightUoM = item.weight[0].unit
      } else {
        remappedItem.weightUoM = !isImperial
          ? METRIC_WEIGHT_UNITS[0].name
          : IMPERIAL_WEIGHT_UNITS[0].name
        remappedItem.weight = ''
      }
      return remappedItem
    })

    localRecord.enriched.items = recordItems
  }

  return localRecord
}
